import { useEffect, useState } from 'react';
import { getAllInvoiceGroups } from 'src/shared/services/invoiceGroupService';
import { getIncomingBankPayments } from 'src/shared/services/incomingBankPaymanetService';
import { getAllPayments } from 'src/shared/services/paymentService';
import { getAllAutogiroMandate } from 'src/shared/services/autogiroMandateService';
import { getActionRequiredCount } from 'src/shared/services/memberAutogiroStatus';
import { getAllElectronicInvoiceAddresses } from 'src/shared/services/electronicInvoiceAddressService';
import { IncomingBankPaymentStatusEnum } from 'src/shared/models/enums/incomingBankPaymentStatus.enum';
import { AutogiroMandateStatusEnum } from 'src/shared/models/enums/autogiroMandateStatus.enum';

export interface ActionRequiredCounts {
  unmatchedBankPayments: number;
  unlinkedMandates: number;
  unaccountedPayments: number;
  membersActionRequired: number;
  donorsActionRequired: number;
  eInvoiceApplications: number;
}

/** Returns counts for the "Att hantera" section on the invoice dashboard */
export const useActionRequiredCounts = (invoiceGroupId?: number) => {
  const [counts, setCounts] = useState<ActionRequiredCounts>({
    unmatchedBankPayments: 0,
    unlinkedMandates: 0,
    unaccountedPayments: 0,
    membersActionRequired: 0,
    donorsActionRequired: 0,
    eInvoiceApplications: 0,
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const fetchCounts = async () => {
      setIsLoading(true);
      try {
        const groupsRes = await getAllInvoiceGroups(0, 500, 'name', 'asc', '');
        const groups = groupsRes.data.results;
        if (!groups.length) {
          setIsLoading(false);
          return;
        }

        const targetGroups = invoiceGroupId ? groups.filter((g) => g.owner_id === invoiceGroupId) : groups;

        // Fetch unmatched bank payments per group
        const unmatchedCounts = await Promise.all(
          targetGroups.map((group) =>
            getIncomingBankPayments(0, 1, 'date', 'desc', '', group.owner_id, IncomingBankPaymentStatusEnum.UnMatched)
              .then((res) => res.data.totalItems)
              .catch(() => 0),
          ),
        );

        // Fetch unlinked autogiro mandates
        const unlinkedRes = await getAllAutogiroMandate({
          pageIndex: 0,
          pageSize: 1,
          sortCol: 'autogiroMandate_id',
          sortDir: 'desc',
          filter: '',
          linkedFilter: 'unlinked',
        }).catch(() => null);

        // Fetch payments without verificationNo (not yet accounted) per group
        const unaccountedCounts = await Promise.all(
          targetGroups.map((group) =>
            getAllPayments({
              pageIndex: 0,
              pageSize: 1,
              sortCol: 'date',
              sortDir: 'desc',
              filter: '',
              ownerId: group.owner_id,
              unaccountedOnly: true,
            })
              .then((res) => res.data.totalItems)
              .catch(() => 0),
          ),
        );

        // Fetch pending e-invoice applications
        const eInvoiceApplicationsRes = await getAllElectronicInvoiceAddresses(
          { pageIndex: 0, pageSize: 1, sortCol: 'eInvoiceAddress_id', sortDir: 'desc', filter: '' },
          true,
        ).catch(() => null);

        // Medlemmar och månadsgivare där dragningen inte blir av, samma uppsättning som flikarnas
        // siffror på autogirosidan: medgivandet är ej godkänt, har fel eller är makulerat.
        const membersRes = await getActionRequiredCount().catch(() => null);

        const donorsRes = await getAllAutogiroMandate({
          pageIndex: 0,
          pageSize: 1,
          sortCol: 'autogiroMandate_id',
          sortDir: 'desc',
          filter: '',
          linkedFilter: 'donor',
          status: [AutogiroMandateStatusEnum.NotApproved, AutogiroMandateStatusEnum.Canceled, AutogiroMandateStatusEnum.Error],
        }).catch(() => null);

        if (cancelled) return;

        setCounts({
          unmatchedBankPayments: unmatchedCounts.reduce((sum, c) => sum + c, 0),
          unlinkedMandates: unlinkedRes?.data.totalItems ?? 0,
          unaccountedPayments: unaccountedCounts.reduce((sum, c) => sum + c, 0),
          membersActionRequired: membersRes?.data ?? 0,
          donorsActionRequired: donorsRes?.data.totalItems ?? 0,
          eInvoiceApplications: eInvoiceApplicationsRes?.data.totalItems ?? 0,
        });
      } catch {
        // Silently fail
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchCounts();
    return () => {
      cancelled = true;
    };
  }, [invoiceGroupId]);

  return { counts, isLoading };
};
